import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/site/Logo";

const LAST_UPDATE = "23 juin 2026";

export default function Confidentialite() {
  return (
    <main
      data-testid="page-confidentialite"
      className="relative font-body text-[#0F172A] bg-[#FAFAFA] min-h-screen"
    >
      {/* Simple header */}
      <header className="border-b border-[#0B1027]/8 bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="page-back-home"
            className="inline-flex items-center gap-3"
            aria-label="Retour à l'accueil"
          >
            <Logo size={36} />
            <span className="font-brand text-xl text-[#0B1027] font-medium tracking-tight uppercase">
              Numedia
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#0B1027]/70 hover:text-[#A855F7] transition-colors"
          >
            <ArrowLeft size={16} />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 md:px-10 py-16 md:py-24">
        <span className="eyebrow text-aurora">— Politique de confidentialité</span>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05]">
          Vos données,{" "}
          <em className="not-italic italic font-light text-aurora">
            traitées avec soin.
          </em>
        </h1>
        <p className="text-[#0F172A]/65 mt-4 text-sm">
          Dernière mise à jour : {LAST_UPDATE}
        </p>

        <div className="prose prose-lg max-w-none mt-12 space-y-10 text-[#0F172A]/85 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              1. Responsable du traitement
            </h2>
            <p>
              Le présent site <strong>numedia.be</strong> est édité par David
              Devos (ci-après « Numedia »), studio indépendant basé à Bruxelles,
              Belgique. Pour toute question relative à vos données personnelles,
              vous pouvez écrire à{" "}
              <a
                href="mailto:contact@numedia.be"
                className="text-[#A855F7] hover:underline font-medium"
              >
                contact@numedia.be
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              2. Données collectées
            </h2>
            <p>
              Numedia ne collecte que les données strictement nécessaires à la
              prise de contact et à la préparation d'un éventuel projet. Les
              données sont collectées via le formulaire de réservation présent
              sur le site :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Nom complet</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone (optionnel)</li>
              <li>Nom de votre activité ou société</li>
              <li>Service souhaité</li>
              <li>Description brève de votre projet</li>
              <li>Créneau de réservation choisi</li>
            </ul>
            <p className="mt-4">
              En plus des données du formulaire, le site utilise{" "}
              <strong>PostHog</strong> (voir section 8) pour des mesures
              d'audience anonymes destinées à améliorer l'expérience de
              navigation. Aucune autre donnée n'est collectée à des fins
              publicitaires.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              3. Finalités du traitement
            </h2>
            <p>Vos données sont utilisées exclusivement pour :</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Prendre contact avec vous suite à votre demande</li>
              <li>Préparer notre échange (compréhension de votre projet)</li>
              <li>Vous transmettre, le cas échéant, une proposition commerciale</li>
              <li>Assurer le suivi d'une relation client si elle s'engage</li>
            </ul>
            <p className="mt-4">
              Vos données ne sont <strong>jamais vendues, louées, échangées
              ni transmises</strong> à des tiers à des fins commerciales.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              4. Base légale (RGPD)
            </h2>
            <p>
              Le traitement de vos données repose sur votre{" "}
              <strong>consentement explicite</strong> (l'envoi volontaire du
              formulaire) ainsi que sur l'<strong>intérêt légitime</strong> de
              Numedia à répondre à une sollicitation commerciale.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              5. Durée de conservation
            </h2>
            <p>
              Les données issues d'une simple demande de contact sans suite
              sont conservées <strong>12 mois maximum</strong>, puis
              supprimées. Si une relation contractuelle s'établit, les données
              sont conservées pendant la durée du projet et jusqu'à 5 ans
              après son terme, conformément aux obligations légales et
              comptables en vigueur en Belgique.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              6. Vos droits
            </h2>
            <p>Conformément au RGPD, vous disposez à tout moment des droits suivants :</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li><strong>Droit d'accès</strong> : connaître les données détenues sur vous</li>
              <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
              <li><strong>Droit à la limitation</strong> du traitement</li>
              <li><strong>Droit à la portabilité</strong> de vos données</li>
              <li><strong>Droit d'opposition</strong> au traitement</li>
            </ul>
            <p className="mt-4">
              Pour exercer ces droits, écrivez simplement à{" "}
              <a
                href="mailto:contact@numedia.be"
                className="text-[#A855F7] hover:underline font-medium"
              >
                contact@numedia.be
              </a>
              . Une réponse vous sera apportée sous 30 jours maximum.
            </p>
            <p className="mt-4">
              Vous avez également le droit d'introduire une réclamation auprès
              de l'Autorité de Protection des Données (APD) belge :{" "}
              <a
                href="https://www.autoriteprotectiondonnees.be"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A855F7] hover:underline font-medium"
              >
                autoriteprotectiondonnees.be
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              7. Sécurité
            </h2>
            <p>
              Vos données sont stockées sur une infrastructure sécurisée
              (Emergent Labs Inc., chiffrement HTTPS de bout en bout).
              Numedia met en œuvre toutes les mesures techniques et
              organisationnelles raisonnables pour protéger vos données
              contre tout accès, modification, divulgation ou destruction non
              autorisés.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              8. Cookies & mesure d'audience
            </h2>
            <p>
              Ce site n'utilise <strong>aucun cookie publicitaire</strong> et
              ne dépose <strong>aucun pixel de traçage tiers</strong> (Meta,
              Google Ads, TikTok, etc.). Vos données ne sont jamais utilisées
              à des fins de profilage publicitaire.
            </p>
            <p className="mt-4">
              Pour comprendre comment les visiteurs naviguent sur le site et
              améliorer leur expérience, Numedia utilise{" "}
              <strong>PostHog</strong>, un outil d'analyse d'audience
              respectueux de la vie privée. PostHog collecte des données
              techniques anonymes telles que :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Type d'appareil, navigateur et système d'exploitation</li>
              <li>Pages visitées et durée de navigation</li>
              <li>Provenance du visiteur (ex. réseau social, moteur de recherche)</li>
              <li>Interactions agrégées (clics, défilement)</li>
            </ul>
            <p className="mt-4">
              Ces données sont <strong>anonymisées et agrégées</strong> :
              elles ne permettent pas de vous identifier personnellement. Elles
              servent uniquement à analyser le comportement global pour
              améliorer le site (lisibilité, vitesse, ergonomie).
            </p>
            <p className="mt-4">
              Vous pouvez à tout moment{" "}
              <strong>désactiver ce suivi</strong> en activant l'option « Do
              Not Track » de votre navigateur, en utilisant une extension de
              blocage (uBlock Origin, Privacy Badger, etc.) ou en navigant en
              mode privé. Pour plus d'informations sur PostHog :{" "}
              <a
                href="https://posthog.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A855F7] hover:underline font-medium"
              >
                posthog.com/privacy
              </a>
              .
            </p>
            <p className="mt-4 text-sm text-[#0F172A]/65 italic">
              Note : PostHog est intégré automatiquement par la plateforme
              d'hébergement Emergent Labs Inc. Aucun autre service de tracking
              n'est utilisé sur ce site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl text-[#0B1027] font-medium tracking-tight mb-4">
              9. Modifications
            </h2>
            <p>
              La présente politique peut être mise à jour pour refléter des
              évolutions techniques ou légales. La date de dernière mise à
              jour figure en haut de cette page.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#0B1027]/10">
          <Link
            to="/"
            data-testid="page-back-home-bottom"
            className="inline-flex items-center gap-2 text-sm text-[#0B1027]/70 hover:text-[#A855F7] transition-colors"
          >
            <ArrowLeft size={16} />
            Retour à l'accueil
          </Link>
        </div>
      </article>
    </main>
  );
}
